import React, { FC, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import { useDialog, useNotification } from '@ccm-office/ccm-ui';
import NavigationPrompt from 'components/NavigationPrompt';
import { DataTable } from './DataTable';
import { httpResponseStatus } from 'communication/httpResponseStatus';
import { IProps } from './types';

export const EditableDataTable: FC<IProps> = ({
  data,
  title,
  columns,
  isRowSelected,
  initialRowValues,
  loading = false,
  onSave,
  onEdit,
  onDelete,
  onRowClick,
  disableEdit,
  disableDelete,
  disableAdd,
  validationSchema,
  disabled,
  onCancel,
  notifyAddEdit,
  sortBy,
  addNewLabel,
}) => {
  const memoizedClone = useCallback(() => {
    return JSON.parse(JSON.stringify(initialRowValues));
  }, [initialRowValues]);

  const [tableData, setTableData] = useState<any[]>(data);
  const [editRowIndex, setEditRowIndex] = useState<number>(-1);
  const [formData, setFormData] = useState<any>(memoizedClone());
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [editRow, setEditRow] = useState<any>(null);

  const dialog = useDialog();
  const notification = useNotification();
  const { t } = useTranslation();

  useEffect(() => {
    setEditRow(null);
    setEditRowIndex(-1);
    setIsAdding(false);
    setTableData(data);
  }, [data]);

  useEffect(() => {
    setFormData(memoizedClone());
  }, [initialRowValues, memoizedClone]);

  const mappedColumns = columns.map((column) => ({
    ...column,
    render: ({ value, rowIndex }: { value: any; rowIndex: number }) => {
      if (isAdding && rowIndex === editRowIndex && !column.disableOnAdd)
        return column.renderEdit(value, column.name, column.placeholder);

      if (rowIndex === editRowIndex && !column.disableOnEdit)
        return column.renderEdit(value, column.name, column.placeholder);

      return column.renderView(value);
    },
  }));

  const handleEdit = (row: any, rowIndex: number, resetForm: (nextState: any) => void) => {
    if (notifyAddEdit) notifyAddEdit(true);

    if (onEdit) onEdit(row);
    row.isEditing = true;

    const clone = JSON.parse(JSON.stringify(row));
    setEditRow(row);
    setEditRowIndex(rowIndex);
    resetForm({
      values: clone,
    });
  };

  const handleCancel = (row: any, rowIndex: number, resetForm: () => void) => {
    if (notifyAddEdit) notifyAddEdit(false);

    if (onCancel) onCancel();

    if (isAdding) {
      const newData = tableData.slice();
      newData.splice(rowIndex, 1);
      setTableData(newData);
    }
    row.isEditing = false;
    setEditRow(null);
    setIsAdding(false);
    setEditRowIndex(-1);
    resetForm();
  };

  const handleAdd = (resetForm: (nextState?: any) => void) => {
    if (notifyAddEdit) notifyAddEdit(true);

    if (onEdit) onEdit(null);

    const newData = tableData.slice();
    newData.splice(0, 0, { ...memoizedClone(), excludeFromSorting: true });
    setTableData(newData);
    resetForm({
      values: { ...memoizedClone(), excludeFromSorting: true },
    });
    setEditRow(null);
    setEditRowIndex(0);
    setIsAdding(true);
  };

  const onSubmit = async (values: any, formikBag: any) => {
    try {
      const result = await onSave(values, isAdding, editRowIndex);
      if (result.success) {
        if (notifyAddEdit) notifyAddEdit(false);

        if (onCancel) onCancel();

        const currentTableData = tableData.map((rowData, index) => {
          if (editRow) {
            return editRow === rowData
              ? { ...values, excludeFromSorting: false, isEditing: false }
              : rowData;
          } else {
            return index === editRowIndex
              ? { ...values, excludeFromSorting: false, isEditing: false }
              : rowData;
          }
        });
        setTableData(currentTableData);
        formikBag.resetForm();
        setFormData(memoizedClone());
        setEditRow(null);
        setEditRowIndex(-1);
        setIsAdding(false);
        notification('Saved!');
      } else {
        const message = result.error ? result.error.message : '';
        const title = result.error
          ? result.error.status === 400
            ? 'Bad Request'
            : 'Error from server'
          : 'Error';

        await dialog({
          title,
          message,
          variant: 'error',
        });
      }
    } catch (e) {
      await dialog({
        title: 'Something went wrong',
        message: 'You can try again',
        variant: 'error',
      });
    }
  };

  const handleDelete = async (row: any, rowIndex: number) => {
    if (!onDelete) return;

    try {
      const result = await onDelete(row, rowIndex);
      if (result.status === httpResponseStatus.success) {
        if (tableData.includes(row)) {
          tableData.splice(rowIndex, 1);
        }
        setTableData([...tableData]);
        setFormData(memoizedClone());
        setEditRow(null);
        setEditRowIndex(-1);
        setIsAdding(false);
        notification('Deleted!');
      } else if (result.status === httpResponseStatus.failure) {
        await dialog({
          title: 'Error from server',
          message: result.error || '',
          variant: 'error',
        });
      }

      return;
    } catch (e) {
      await dialog({
        title: 'Something went wrong',
        message: 'You can try again',
        variant: 'error',
      });
    }
  };

  const getEnabledTools = (resetForm: (nextState: any) => void) => {
    const toolsArr: any = [];

    const addTool = {
      label: 'Add',
      text: addNewLabel ? addNewLabel : `${t('button.add')} ${title || ''}`,
      icon: 'add',
      onClick: () => handleAdd(resetForm),
      disabled: editRowIndex > -1 || disabled,
    };

    if (disableAdd !== true) toolsArr.push(addTool);

    return toolsArr;
  };

  const getEnabledActions = (isSubmitting: boolean, submitForm: any, resetForm: any) => {
    const actionsArr: any = [];

    const editAction = {
      label: 'Edit',
      icon: 'edit',
      onClick: (row: any, rowIndex: number) => handleEdit(row, rowIndex, resetForm),
      visible: () => editRowIndex === -1 && !disabled,
    };

    const cancelAction = {
      label: 'Cancel',
      icon: 'cancel',
      onClick: (row: any, rowIndex: number) => handleCancel(row, rowIndex, resetForm),
      visible: (rowIndex: number) => editRowIndex === rowIndex,
    };

    const saveAction = {
      label: 'Save',
      icon: 'save',
      onClick: submitForm,
      visible: (rowIndex: number) => editRowIndex === rowIndex,
      loading: isSubmitting,
    };

    const deleteAction = {
      label: 'Delete',
      icon: 'delete',
      onClick: handleDelete,
      visible: () => editRowIndex === -1,
      loading: isSubmitting,
    };

    if (disableEdit !== true) {
      actionsArr.push(editAction);
      actionsArr.push(saveAction);
      actionsArr.push(cancelAction);
    }

    if (disableDelete !== true) actionsArr.push(deleteAction);

    if (disableAdd !== true && disableEdit === true) {
      actionsArr.push(saveAction);
      actionsArr.push(cancelAction);
    }
    return actionsArr;
  };

  return (
    <Formik
      initialValues={formData}
      enableReinitialize
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {({ dirty, isSubmitting, submitForm, resetForm, values }) => (
        <Form>
          <NavigationPrompt when={dirty} />
          <DataTable
            title={title}
            isRowSelected={isRowSelected}
            data={tableData}
            actions={getEnabledActions(isSubmitting, submitForm, resetForm)}
            columns={mappedColumns}
            tools={getEnabledTools(resetForm)}
            onRowClick={onRowClick}
            defaultSortBy={sortBy}
            disableSorting={editRowIndex > -1}
            loading={loading}
          />
        </Form>
      )}
    </Formik>
  );
};
